export interface StyleSignal {
  label: string;
  detail: string;
}

export interface TextAnalysis {
  characters: number;
  charactersWithoutSpaces: number;
  words: number;
  sentences: number;
  paragraphs: number;
  listItems: number;
  links: number;
  readingMinutes: number;
  averageSentenceWords: number;
  longestSentenceWords: number;
  lexicalDiversityPercent: number;
  readability: 'leicht' | 'mittel' | 'anspruchsvoll';
  styleSignals: StyleSignal[];
  styleSignalLevel: 'keine' | 'wenige' | 'mehrere';
}

const phraseGroups: Array<{ label: string; patterns: RegExp[] }> = [
  {
    label: 'Formelhafte Einleitung',
    patterns: [
      /\bin der heutigen (?:zeit|welt|gesellschaft)\b/gi,
      /\bin einer (?:sich )?ständig (?:wandelnden|verändernden) welt\b/gi,
      /\bin diesem (?:artikel|beitrag|text) (?:werden|möchten wir|geht es)\b/gi,
    ],
  },
  {
    label: 'Typische Hinweisformel',
    patterns: [
      /\bes ist wichtig zu (?:beachten|betonen|erwähnen)\b/gi,
      /\bes lässt sich festhalten,? dass\b/gi,
      /\bdabei gilt es zu beachten\b/gi,
    ],
  },
  {
    label: 'Formelhafter Abschluss',
    patterns: [
      /\bzusammenfassend lässt sich sagen\b/gi,
      /\babschließend lässt sich festhalten\b/gi,
      /\bim großen und ganzen\b/gi,
    ],
  },
];

const transitionPattern = /\b(?:darüber hinaus|des weiteren|zudem|außerdem|nicht zuletzt|gleichzeitig|dennoch|hingegen|abschließend|zusammenfassend)\b/gi;

function wordsIn(value: string): string[] {
  return value.match(/[\p{L}\p{M}]+(?:[’'-][\p{L}\p{M}]+)*/gu) ?? [];
}

function sentencesIn(value: string): string[] {
  const normalized = value.replace(/\s*\n+\s*/g, ' ').trim();
  if (!normalized) return [];
  return normalized.match(/[^.!?…]+(?:[.!?…]+|$)/g)?.map((sentence) => sentence.trim()).filter(Boolean) ?? [];
}

function standardDeviation(values: number[], average: number): number {
  if (values.length === 0) return 0;
  return Math.sqrt(values.reduce((total, value) => total + ((value - average) ** 2), 0) / values.length);
}

export function analyzeText(input: string): TextAnalysis {
  const text = input.replace(/\r\n?/g, '\n').trim();
  const words = wordsIn(text);
  const sentences = sentencesIn(text);
  const sentenceLengths = sentences.map((sentence) => wordsIn(sentence).length).filter((length) => length > 0);
  const averageSentenceWords = sentenceLengths.length > 0
    ? sentenceLengths.reduce((total, length) => total + length, 0) / sentenceLengths.length
    : 0;
  const longWordRatio = words.length > 0 ? words.filter((word) => word.length >= 12).length / words.length : 0;
  const readability: TextAnalysis['readability'] = averageSentenceWords <= 14 && longWordRatio < 0.12
    ? 'leicht'
    : averageSentenceWords <= 22 && longWordRatio < 0.2 ? 'mittel' : 'anspruchsvoll';
  const styleSignals: StyleSignal[] = [];

  for (const group of phraseGroups) {
    const count = group.patterns.reduce((total, pattern) => total + (text.match(pattern)?.length ?? 0), 0);
    if (count > 0) styleSignals.push({ label: group.label, detail: `${count} passende Formulierung${count === 1 ? '' : 'en'} gefunden.` });
  }

  const transitions = text.match(transitionPattern)?.length ?? 0;
  if (transitions >= 4 && transitions >= Math.ceil(sentences.length * 0.3)) {
    styleSignals.push({ label: 'Viele Übergangswörter', detail: `${transitions} stark strukturierende Übergänge im Text.` });
  }

  if (sentenceLengths.length >= 6 && averageSentenceWords >= 7) {
    const variation = standardDeviation(sentenceLengths, averageSentenceWords) / averageSentenceWords;
    if (variation < 0.24) {
      styleSignals.push({ label: 'Gleichförmige Satzlängen', detail: 'Die Satzlängen variieren ungewöhnlich wenig.' });
    }
  }

  if (sentences.length >= 5) {
    const starts = sentences
      .map((sentence) => wordsIn(sentence).slice(0, 2).join(' ').toLocaleLowerCase('de-DE'))
      .filter((start) => start.split(' ').length === 2);
    const repetitions = starts.filter((start, index) => starts.indexOf(start) !== index);
    const uniqueRepetitions = new Set(repetitions);
    if (uniqueRepetitions.size > 0) {
      styleSignals.push({ label: 'Wiederholte Satzanfänge', detail: `${uniqueRepetitions.size} Satzanfang${uniqueRepetitions.size === 1 ? '' : 'sgruppen'} wiederholt.` });
    }
  }

  const uniqueWords = new Set(words.map((word) => word.toLocaleLowerCase('de-DE')));
  return {
    characters: text.length,
    charactersWithoutSpaces: text.replace(/\s/g, '').length,
    words: words.length,
    sentences: sentences.length,
    paragraphs: text ? text.split(/\n\s*\n/).filter((paragraph) => paragraph.trim()).length : 0,
    listItems: text.match(/^\s*(?:[-*+•‣▪◦]|\d+[.)])\s+/gm)?.length ?? 0,
    links: text.match(/\b(?:https?:\/\/|www\.)[^\s<]+/gi)?.length ?? 0,
    readingMinutes: words.length === 0 ? 0 : Math.max(1, Math.ceil(words.length / 220)),
    averageSentenceWords: Math.round(averageSentenceWords * 10) / 10,
    longestSentenceWords: sentenceLengths.length > 0 ? Math.max(...sentenceLengths) : 0,
    lexicalDiversityPercent: words.length > 0 ? Math.round((uniqueWords.size / words.length) * 100) : 0,
    readability,
    styleSignals,
    styleSignalLevel: styleSignals.length === 0 ? 'keine' : styleSignals.length <= 2 ? 'wenige' : 'mehrere',
  };
}
